// The vendor panel's purchase orders: PO defaults (deposit, usual ship-to), "New PO from this
// vendor's items", and the vendor's POs as the usual table (it scrolls inside past 12; its View and Columns in the row
// above it), each opening in the side pane.
import { ListPlus } from "lucide-react";
import { useMemo, useState } from "react";
import type { Po } from "../../../../shared/db.gen.ts";
import { PO_STATUS_LABELS, PO_STATUSES, poTotals } from "../../../../shared/po.ts";
import { dayText, money } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRow, useRows } from "../../lib/sync.ts";
import { Badge, Button } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { Fields, Section } from "../../ui/RecordPanel.tsx";
import { PaneLink, usePaneOpen } from "../../ui/SidePane.tsx";
import { poPath, STATUS_TONE, useLinesByPo } from "./lib.ts";
import { NeededBy } from "./NeededBy.tsx";
import { NewPoDialog } from "./NewPo.tsx";
import { useShipToOptions } from "./ShipTo.tsx";

/** Past this many, a link to all of them on the POs page too (the table shows them all, scrolling inside). */
const SHOWN = 12;

type Row = Po & { lines: number; subtotal: number };

export function VendorPos({ vendorId }: { vendorId: string }) {
  const vendor = useRow("vendor", vendorId);
  const allPos = useRows("po");
  const linesByPo = useLinesByPo();
  const shipTo = useShipToOptions(vendor?.default_ship_to_location_id);
  const [picking, setPicking] = useState(false);
  const openPane = usePaneOpen();
  const pos = useMemo<Row[]>(
    () =>
      allPos
        .filter((p) => p.vendor_id === vendorId)
        .map((p) => {
          const totals = poTotals(linesByPo.get(p.id) ?? [], p.deposit_pct);
          return { ...p, lines: totals.lines, subtotal: totals.subtotal };
        }),
    [allPos, vendorId, linesByPo],
  );
  if (!vendor) return null;
  const columns: Column<Row>[] = [
    {
      key: "number",
      header: "PO",
      width: "90px",
      cell: (p) => (
        <PaneLink kind="po" id={p.id} to={poPath(p.id)} className="font-mono text-[12px] font-medium hover:underline">
          {p.number}
        </PaneLink>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      value: (p) => PO_STATUSES.indexOf(p.status),
      text: (p) => PO_STATUS_LABELS[p.status],
      cell: (p) => <Badge tone={STATUS_TONE[p.status]}>{PO_STATUS_LABELS[p.status]}</Badge>,
    },
    { key: "lines", header: "Lines", width: "64px", align: "right", cell: (p) => <span className="text-muted">{p.lines}</span> },
    {
      key: "when",
      header: "Needed by",
      headerTitle: "When it's needed by, else when it ships",
      width: "minmax(130px,1fr)",
      value: (p) => p.expected_arrive ?? p.expected_ship ?? "",
      text: (p) => (p.expected_arrive ? dayText(p.expected_arrive) : p.expected_ship ? `ships ${dayText(p.expected_ship)}` : ""),
      cell: (p) => (
        <span className="truncate text-muted">
          {p.expected_arrive ? <NeededBy po={p} prefix="" /> : p.expected_ship ? `ships ${dayText(p.expected_ship)}` : ""}
        </span>
      ),
    },
    {
      key: "subtotal",
      header: "Subtotal",
      width: "110px",
      align: "right",
      text: (p) => money(p.subtotal, p.currency),
      cell: (p) => <span className="font-mono text-[12px]">{money(p.subtotal, p.currency)}</span>,
    },
    {
      key: "created_at",
      header: "Made",
      width: "84px",
      optional: true,
      text: (p) => dayText(p.created_at),
      cell: (p) => <span className="text-muted">{dayText(p.created_at)}</span>,
    },
  ];

  return (
    <Section title="Purchase orders" count={pos.length} addLabel="New PO" onAdd={can("po.create") && !vendor.archived ? () => setPicking(true) : undefined}>
      <div className="flex flex-col gap-0.5">
        <Fields
          compact
          row={vendor}
          update="vendor.update_po_defaults"
          disabled={!can("vendor.update_po_defaults")}
          fields={[
            {
              key: "deposit_pct",
              label: "Deposit %",
              type: "number",
              format: (v) => (v ? `${v}%` : "None"),
              save: (v) => mutate("vendor.update_po_defaults", { id: vendor.id, patch: { deposit_pct: v == null ? 0 : Number(v) } }),
            },
            {
              key: "default_ship_to_location_id",
              label: "Ships to",
              type: "select",
              nullable: true,
              options: [{ value: "", label: "HHL (default)" }, ...shipTo.map((o) => ({ value: o.value, label: o.label }))],
            },
          ]}
        />
      </div>
      {pos.length > 0 && (
        <div className="px-2">
          <DataTable
            fit
            rows={pos}
            columns={columns}
            defaultSort={{ key: "created_at", desc: true }}
            onOpen={(p) => openPane("po", p.id)}
            exportName="Vendor POs"
            pageFilters={false}
            rowNoun={["PO", "POs"]}
          />
        </div>
      )}
      <div className="px-2 pt-1 pb-2">
        {pos.length > SHOWN && (
          <Link to={`/pos?vendor=${vendorId}&status=all`} className="block px-2 py-1 text-[12px] text-accent-text hover:underline">
            All {pos.length} POs
          </Link>
        )}
        {!pos.length && can("po.create") && !vendor.archived && (
          <Button size="sm" variant="ghost" icon={<ListPlus className="size-3.5" />} onClick={() => setPicking(true)}>
            Start their first PO
          </Button>
        )}
      </div>
      {/* The one New PO dialog: their open draft opens, else a new one, to add their items to there */}
      {picking && <NewPoDialog vendorId={vendor.id} onClose={() => setPicking(false)} />}
    </Section>
  );
}
